import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import LoadingApp from '../components-library/loading/LoadingApp';
import { AnimatePresence } from 'framer-motion';
import ConfigManager from '../services/api/ConfigManager';
import { ConfigInterface } from '../services/exports/Interfaces';
import { CaptchaMode } from '../services/exports/Constants';
import { LoadingContext } from './LoadingContext';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import useStorefront from '../hooks/storefront/useStorefront';
import useMenu from '../hooks/menu/useMenu';

interface Props {
  children: ReactElement;
}

export { LoadingContext } from './LoadingContext';
export type { ILoadingContext } from './LoadingContext';

export const SPLASH_KEY = 'app-splash';

/**
 * How long a login form waits for the store config before the captcha falls back to direct. The
 * config client has no timeout, so a request that never settles would otherwise leave every login
 * and sign-up form without a captcha, on the platform roots too. Direct is what those roots need,
 * and a config that lands later still wins.
 */
export const CAPTCHA_MODE_FALLBACK_MS = 5000;

/**
 * Only an explicit `iframe` frames the captcha. Anything else is direct: a config that predates the
 * field, the native app's copy until it passes the field through, and no config at all after a
 * failed request. The platform roots are allow-listed, so they keep a captcha that works.
 */
function captchaModeOf(config: ConfigInterface | null): CaptchaMode {
  return config?.captcha_mode === CaptchaMode.Iframe ? CaptchaMode.Iframe : CaptchaMode.Direct;
}

const LoadingProvider = (props: Props): ReactElement => {
  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { loading: loadingStorefront } = useStorefront();
  const { loadingMenu } = useMenu();

  const mobileAppConfig = window?.mobileAppConfig?.data?.config;
  const [config, setConfig] = useState<ConfigInterface | null>(mobileAppConfig ?? null);
  const [loading, setLoading] = useState(mobileAppConfig ? false : true);
  // The native app injects its config, so there is nothing to wait for there.
  const [configSettled, setConfigSettled] = useState(Boolean(mobileAppConfig));
  const isLoading = loading || loadingStorefront || loadingMenu;

  /**
   * Null until the config is known, rather than a guess at direct. Children render under the
   * splash from the first paint, so a login form can mount before the config lands. On a
   * partner's own root that guess started a widget the vendor refuses there, let its hCaptcha
   * fallback fail too, and only then switched to the iframe.
   */
  const captchaMode = configSettled ? captchaModeOf(config) : null;
  const captchaFallbackTimer = useRef<number | null>(null);

  const clearCaptchaFallback = (): void => {
    if (captchaFallbackTimer.current !== null) {
      clearTimeout(captchaFallbackTimer.current);
      captchaFallbackTimer.current = null;
    }
  };

  async function requestConfig(): Promise<void> {
    try {
      const { response, success } = await ConfigManager.get();

      if (success && response?.data?.data) {
        setConfig(response.data.data);
      }
    } finally {
      // Settled either way. A mode that stayed null would leave every login form without a captcha.
      clearCaptchaFallback();
      setConfigSettled(true);
    }
  }

  useEffect(() => {
    if (mobileAppConfig) {
      return;
    }

    requestConfig();

    // A request that never settles must not hold the captcha back for good; see the constant.
    captchaFallbackTimer.current = window.setTimeout(() => setConfigSettled(true), CAPTCHA_MODE_FALLBACK_MS);

    return clearCaptchaFallback;
  }, []);

  /**
   * One timer, not two. `requestConfig` used to schedule the same `setLoading(false)` off a
   * `company?.id` it had captured on the first render — normally undefined, so it did nothing,
   * and when it did fire it raced this effect's timer. Two timers meant two chances to flip
   * `isLoading` inside the splash's 0.7s exit animation, which is what the AnimatePresence
   * overlap below is guarding against. Clearing on unmount also stops the setState-after-unmount.
   */
  useEffect(() => {
    if (!company?.id) {
      return;
    }

    const timer = setTimeout(() => setLoading(false), 1000);

    return () => clearTimeout(timer);
  }, [company?.id]);

  /**
   * The `key` is load-bearing, not decoration. framer-motion v6 keys its presence bookkeeping by
   * `child.key || ""`, so a keyless child collapses every enter/exit cycle onto one slot in
   * `allChildren`/`presentChildren`. `isLoading` is three independently-flipping flags OR'd
   * together, so it can go false -> true again inside the 0.7s exit animation; with the shared ""
   * key those two cycles overwrite each other's bookkeeping, and when React finally deletes the
   * stale PresenceChild the DOM node is no longer under the parent it recorded:
   * "Failed to execute 'removeChild' on 'Node'" (Sentry FOODAMIGOS-SHOP-WEB-1Z4 / -1ZS, which
   * crashes the whole app into Sentry.ErrorBoundary's ErrorScreen). A stable key makes
   * framer-motion recognise the re-entering child and skip the exit instead.
   */
  const renderLoadingApp = () => (
    <AnimatePresence>
      {isLoading && <LoadingApp key={SPLASH_KEY} color={config?.primary_color} logo={config?.logo_url} />}
    </AnimatePresence>
  );

  const context = useMemo(
    () => ({
      loading: isLoading,
      setLoading,
      renderLoadingApp,
      captchaMode,
    }),
    [loading, loadingStorefront, loadingMenu, captchaMode],
  );

  return (
    <LoadingContext.Provider value={context}>
      {children}
      {renderLoadingApp()}
    </LoadingContext.Provider>
  );
};

export default LoadingProvider;
